import type { ComponentType, ReactNode, SVGProps } from 'react';
import {
    CalendarDaysIcon,
    ChatBubbleBottomCenterTextIcon,
    FlagIcon,
    UserCircleIcon
} from '@heroicons/react/24/outline';
import { OverlayScrollArea, toDateOnly, type TiptapDoc } from '@odo/shared-ui';
import type { VirtualAppointmentCancellation } from '../../../services/virtualAppointments';
import type {
    VirtualAppointmentOverviewItem,
    VirtualAppointmentOverviewPerson
} from '../../../services/virtualAppointmentsOverview';
import { TeamPill } from '@odo/shared-ui/components/TeamPill';
import { buildWorkQueueCaseUrl } from '../../../utils/workQueueLink';
import { VirtualAppointmentReadOnlyDescription } from '../../virtual-appointments/VirtualAppointmentDetailPanels';
import { VirtualAppointmentStatusChip } from '../../virtual-appointments/VirtualAppointmentStatusChip';
import { VirtualAppointmentAssigneeValue } from '../../virtual-appointments/VirtualAppointmentAssignee';
import { VirtualAppointmentDueBadge } from '../../virtual-appointments/VirtualAppointmentDueBadge';
import { formatSummaryTrigger } from '@odo/shared-ui/clinical/virtualAppointments/summary';
import { summaryFromDto } from '../../virtual-appointments/virtualAppointmentSummary';
import { AppointmentDateDisplay } from '../appointments/AppointmentDateDisplay';
import { AppointmentDetailField } from '../appointments/AppointmentDetailField';
import {
    AppointmentPatientBlock,
    type AppointmentPatientSource
} from '../appointments/AppointmentPatientBlock';
import { OverviewMonitoringSection } from './OverviewMonitoringSection';
import { MonitoringTag } from './VirtualAppointmentOverviewCells';
import { TIER_TEXT_CLASS, type LatenessTier } from '../timeThresholds';
import { LATE_REASON_TEXT, lateDistance } from './lateDistance';
import { monitoringStateOf } from './monitoringState';
import { triggerHint } from './triggerHint';
import { cancellationReasonText, closureLabels } from './closure';
import { formatPerson } from './person';

export type DescriptionState = 'loading' | 'ready' | 'error';

const VALUE_BOX_BASE =
    'w-full flex gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800';
const VALUE_BOX = `${VALUE_BOX_BASE} items-center`;

function toPatientSource(
    item: VirtualAppointmentOverviewItem,
    doctorName: string | null
): AppointmentPatientSource {
    const { patient } = item;
    return {
        patientId: patient.id,
        patientName: patient.name,
        profilePictureUrl: null,
        practiceId: patient.practiceId ?? undefined,
        practiceName: patient.practiceName ?? undefined,
        practiceDoctorName: doctorName,
        patientInternalStatus: patient.internalCaseStatus
    };
}

function dateOnly(iso: string): string | null {
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? null : toDateOnly(date);
}

interface TriggerValueProps {
    item: VirtualAppointmentOverviewItem;
    todayIso: string;
}

function TriggerValue({ item, todayIso }: TriggerValueProps) {
    const Icon: ComponentType<SVGProps<SVGSVGElement>> =
        item.triggerKind === 'weeks' ? CalendarDaysIcon : FlagIcon;
    const text = formatSummaryTrigger(summaryFromDto(item));
    const late = lateDistance(item, todayIso);
    return (
        <div className={VALUE_BOX}>
            <Icon className="h-4 w-4 text-gray-400 shrink-0" />
            <span className={`flex-1 truncate ${late ? TIER_TEXT_CLASS[late.tier] : ''}`}>
                {text}
            </span>
            {item.isDue && <VirtualAppointmentDueBadge />}
            <MonitoringTag item={item} />
        </div>
    );
}

// Who works the VA: its team, and the person the patient goes to when it fires. Borderless values.
function Assignment({ item }: { item: VirtualAppointmentOverviewItem }) {
    return (
        <div className="grid grid-cols-2 gap-3">
            <AppointmentDetailField label="Team">
                <div className="flex items-center h-7 text-sm">
                    <TeamPill team={item.team} size="xs" />
                </div>
            </AppointmentDetailField>
            <AppointmentDetailField label="Assignee">
                <div className="flex items-center h-7 text-sm">
                    <VirtualAppointmentAssigneeValue
                        team={item.team}
                        assigneeEmail={item.assigneeEmail}
                    />
                </div>
            </AppointmentDetailField>
        </div>
    );
}

interface LateNoticeProps {
    item: VirtualAppointmentOverviewItem;
    todayIso: string;
}

const NOTICE_TONE: Record<LatenessTier, string> = {
    late: 'border-yellow-200 bg-yellow-50 text-yellow-800',
    moderate: 'border-orange-200 bg-orange-50 text-orange-800',
    extreme: 'border-red-200 bg-red-50 text-red-800'
};

// Why the VA is stuck, at the foot of the pane in the colour of its lateness.
function LateNotice({ item, todayIso }: LateNoticeProps) {
    const distance = lateDistance(item, todayIso);
    if (!distance || !item.late) return null;
    return (
        <p className={`rounded-lg border px-3 py-2 text-xs ${NOTICE_TONE[distance.tier]}`}>
            <span className="font-semibold">{distance.ago ? `Due ${distance.ago}` : 'Late'}</span>
            {' · '}
            {LATE_REASON_TEXT[item.late.reason]}
        </p>
    );
}

// Only a VA still waiting on its trigger is affected; once reached or opened, the pause no longer matters.
function isPausedBeforeTrigger(item: VirtualAppointmentOverviewItem): boolean {
    const waiting = item.status === 'planned' && !item.isDue;
    return waiting && monitoringStateOf(item.patient.monitoringStatus) === 'paused';
}

function pausedText(item: VirtualAppointmentOverviewItem): string {
    switch (item.triggerKind) {
        case 'aligner':
            return "The aligner count isn't moving, so this VA won't come due until monitoring resumes.";
        case 'nogos':
            return "No scans come in, so the no-go count won't move until monitoring resumes.";
        default:
            return "This VA still comes due at its week, but the patient isn't sending scans.";
    }
}

// Paused monitoring freezes the aligner and no-go counts, so those triggers wait for it to resume.
function PausedNotice({ item }: { item: VirtualAppointmentOverviewItem }) {
    if (!isPausedBeforeTrigger(item)) return null;
    return (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <span className="font-semibold">Monitoring paused</span>
            {' · '}
            {pausedText(item)}
        </p>
    );
}

// First under the header, only when something needs attention.
function Warnings({ item, todayIso }: LateNoticeProps) {
    const late = lateDistance(item, todayIso) !== null;
    if (!late && !isPausedBeforeTrigger(item)) return null;
    return (
        <AppointmentDetailField label="Warnings">
            <div className="space-y-2">
                <LateNotice item={item} todayIso={todayIso} />
                <PausedNotice item={item} />
            </div>
        </AppointmentDetailField>
    );
}

interface PersonValueProps {
    person: VirtualAppointmentOverviewPerson | null;
    loading: boolean;
}

function ValuePulse({ label }: { label: string }) {
    return <span aria-label={label} className="h-3 w-28 bg-gray-200 rounded animate-pulse" />;
}

// People only arrive with the detail fetch, so a list-row pane waits on them like the description.
function PersonValue({ person, loading }: PersonValueProps) {
    const { label, title } = formatPerson(person);
    return (
        <div className={VALUE_BOX}>
            <UserCircleIcon className="h-4 w-4 text-gray-400 shrink-0" />
            {loading ? (
                <ValuePulse label="Loading person" />
            ) : (
                <span className="flex-1 truncate" title={title}>
                    {label}
                </span>
            )}
        </div>
    );
}

interface ReasonValueProps {
    cancellation: VirtualAppointmentCancellation | null;
    loading: boolean;
}

// A staff reason can run long, so it wraps in full instead of truncating like the person rows.
function ReasonValue({ cancellation, loading }: ReasonValueProps) {
    return (
        <div className={`${VALUE_BOX_BASE} items-start`}>
            <ChatBubbleBottomCenterTextIcon className="h-4 w-4 mt-0.5 text-gray-400 shrink-0" />
            {loading ? (
                <span className="flex h-5 items-center">
                    <ValuePulse label="Loading reason" />
                </span>
            ) : (
                <p className="flex-1 min-w-0 whitespace-pre-wrap break-words">
                    {cancellationReasonText(cancellation)}
                </p>
            )}
        </div>
    );
}

interface ClosureProps {
    item: VirtualAppointmentOverviewItem;
    resolvedBy: VirtualAppointmentOverviewPerson | null;
    cancellation: VirtualAppointmentCancellation | null;
    loading: boolean;
}

function Closure({ item, resolvedBy, cancellation, loading }: ClosureProps) {
    const labels = closureLabels(item.status);
    const closedOn = labels ? dateOnly(item.resolvedAt ?? item.updatedAt) : null;
    if (!labels || !closedOn) return null;
    return (
        <>
            <AppointmentDetailField label={labels.date}>
                <AppointmentDateDisplay date={closedOn} />
            </AppointmentDetailField>
            <AppointmentDetailField label={labels.by}>
                <PersonValue person={resolvedBy} loading={loading} />
            </AppointmentDetailField>
            {labels.reason && (
                <AppointmentDetailField label={labels.reason}>
                    <ReasonValue cancellation={cancellation} loading={loading} />
                </AppointmentDetailField>
            )}
        </>
    );
}

interface DescriptionProps {
    doc: TiptapDoc | null;
    fallbackText: string | null;
    state: DescriptionState;
    onRetry: () => void;
}

function DescriptionBox({ children }: { children: ReactNode }) {
    return (
        <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm">
            {children}
        </div>
    );
}

function Description({ doc, fallbackText, state, onRetry }: DescriptionProps) {
    if (state === 'loading') {
        return (
            <DescriptionBox>
                <div className="space-y-2" aria-label="Loading description">
                    <div className="h-3 w-5/6 bg-gray-200 rounded animate-pulse" />
                    <div className="h-3 w-2/3 bg-gray-200 rounded animate-pulse" />
                </div>
            </DescriptionBox>
        );
    }
    if (doc) {
        return (
            <DescriptionBox>
                <VirtualAppointmentReadOnlyDescription doc={doc} layout="plain" />
            </DescriptionBox>
        );
    }
    if (state === 'error' && !fallbackText) {
        return (
            <DescriptionBox>
                <span className="text-gray-500">Couldn&rsquo;t load the description. </span>
                <button type="button" onClick={onRetry} className="text-blue-600 hover:underline">
                    Retry
                </button>
            </DescriptionBox>
        );
    }
    return (
        <DescriptionBox>
            {fallbackText ? (
                <p className="whitespace-pre-wrap text-gray-800">{fallbackText}</p>
            ) : (
                <span className="text-gray-400 italic">No description</span>
            )}
        </DescriptionBox>
    );
}

interface VirtualAppointmentOverviewDetailProps {
    item: VirtualAppointmentOverviewItem;
    createdBy: VirtualAppointmentOverviewPerson | null;
    resolvedBy: VirtualAppointmentOverviewPerson | null;
    cancellation: VirtualAppointmentCancellation | null;
    doctorName: string | null;
    todayIso: string;
    description: Omit<DescriptionProps, 'fallbackText'>;
}

export function VirtualAppointmentOverviewDetail({
    item,
    createdBy,
    resolvedBy,
    cancellation,
    doctorName,
    todayIso,
    description
}: VirtualAppointmentOverviewDetailProps) {
    const summary = summaryFromDto(item);
    const created = dateOnly(item.createdAt);
    const peopleLoading = description.state === 'loading';
    return (
        <OverlayScrollArea
            className="flex-1 min-h-0"
            // The shared appointment fields are 14px; the VA pane reads at 13px like the other VA
            // screens, the 15px title aside.
            contentClassName="px-5 py-5 [&_.text-sm]:text-[13px] [&_.prose]:text-[13px]"
            innerClassName="space-y-5"
            trackInsetPx={6}
        >
            {/* The status shares the label's line, right under the pane's floating close button. */}
            <div>
                <h3 className="pr-8 text-[15px] font-semibold text-gray-800 break-words">
                    {summary.typeTitle}
                </h3>
                <div className="flex items-center justify-between gap-3 mt-0.5">
                    <p className="min-w-0 text-sm text-gray-500">{summary.typeLabel}</p>
                    <VirtualAppointmentStatusChip status={item.status} size="xs" />
                </div>
            </div>
            <Warnings item={item} todayIso={todayIso} />
            <AppointmentDetailField label="Patient">
                <AppointmentPatientBlock
                    appointment={toPatientSource(item, doctorName)}
                    href={buildWorkQueueCaseUrl(item.patient.id)}
                />
            </AppointmentDetailField>
            <Assignment item={item} />
            <AppointmentDetailField
                label="Trigger"
                hint={lateDistance(item, todayIso)?.ago ?? triggerHint(item, todayIso) ?? undefined}
            >
                <TriggerValue item={item} todayIso={todayIso} />
            </AppointmentDetailField>
            <OverviewMonitoringSection patient={item.patient} />
            {created && (
                <AppointmentDetailField label="Created">
                    <AppointmentDateDisplay date={created} />
                </AppointmentDetailField>
            )}
            <AppointmentDetailField label="Created by">
                <PersonValue person={createdBy} loading={peopleLoading} />
            </AppointmentDetailField>
            <Closure
                item={item}
                resolvedBy={resolvedBy}
                cancellation={cancellation}
                loading={peopleLoading}
            />
            <AppointmentDetailField label="Description">
                <Description {...description} fallbackText={item.description} />
            </AppointmentDetailField>
        </OverlayScrollArea>
    );
}
